import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Paper, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LayersIcon from '@mui/icons-material/Layers';
import RefreshIcon from '@mui/icons-material/Refresh';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, sectionCardSx, listCardSx, sectionHeadingSx, emptyTextSx, sectionGapSx,
  actionRowSx, dialogProps, dialogTitleSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import { disableStackSets, listStackSets } from '../../../services/stacksetsApi';
import RoleStatusPanel from './RoleStatusPanel';
import { POLL_INTERVAL_MS } from '../securityhub/pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';

export const statusColor = (status) => ({
  active: 'success',
  partial_failure: 'warning',
  failed: 'error',
  deleted: 'default',
  in_progress: 'info',
  updating: 'info',
  deleting: 'info',
}[status] || 'default');

const ACTIVE_STATUSES = ['in_progress', 'updating', 'deleting'];

// Switch/checkbox labels inherit MUI's body1; the portal writes them in
// Metropolis at the same size as its table cells.
const controlLabelSx = {
  '& .MuiFormControlLabel-label': {
    fontFamily: FONT_BODY,
    fontSize: '0.875rem',
    color: CK.body,
  },
};

export default function StackSetsList({ config, onConfigChange }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [deleteBucket, setDeleteBucket] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [disableErr, setDisableErr] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await listStackSets();
      setRows(res?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while any row is transitioning (EnrollmentPage pattern): the starting
  // effect is keyed on a derived boolean primitive, so a row that becomes
  // active after mount (e.g. returning from the create wizard, or a later
  // action) re-arms the poll. The interval stops itself inside the tick only
  // when a SUCCESSFUL response confirms quiescence — a failed fetch keeps
  // polling. Deliberately no cleanup in this effect: setRows on each tick
  // would otherwise destroy-and-recreate the interval; a separate
  // unmount-only effect handles cleanup.
  const hasActive = !!rows?.some((r) => ACTIVE_STATUSES.includes(r.status));
  useEffect(() => {
    if (!hasActive || pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const res = await listStackSets().catch(() => null);
      if (!res) return; // transient error — keep polling
      const next = res?.data || [];
      setRows(next);
      const anyActive = next.some((r) => ACTIVE_STATUSES.includes(r.status));
      if (!anyActive && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, POLL_INTERVAL_MS);
  }, [hasActive]);

  // Stop the interval when the component unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const pageHeader = (
    <PortalPageHeader
      icon={<LayersIcon sx={{ fontSize: 24 }} />}
      title="CloudFormation StackSets"
      subtitle="Deploy templates to accounts across your organization's OUs"
      action={(
        <Box sx={actionRowSx}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refresh()}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/stacksets/create')}>
            Create StackSet
          </Button>
        </Box>
      )}
    />
  );

  // Error and loading keep the page header mounted and swap only what sits
  // under it, so the layout does not jump once the list lands.
  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{pageHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={refresh} />
        </motion.div>
      </motion.div>
    );
  }
  if (!rows) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{pageHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading StackSets…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  const visible = rows.filter((r) => showDeleted || r.status !== 'deleted');
  const parseOUs = (raw) => { try { return JSON.parse(raw || '[]'); } catch (_) { return []; } };
  const parseRegions = (raw) => { try { return JSON.parse(raw || '[]'); } catch (_) { return []; } };

  // The backend blocks disable while any non-deleted StackSet exists; disable
  // the control proactively with the same rule so the user isn't invited into
  // a guaranteed error.
  const liveCount = rows.filter((r) => r.status !== 'deleted').length;

  const openDisable = () => {
    setDeleteBucket(false); // destructive option must not be sticky across opens
    setDisableErr(null);
    setDisableOpen(true);
  };

  const doDisable = async () => {
    setDisabling(true);
    setDisableErr(null);
    try {
      await disableStackSets(deleteBucket);
      setDisableOpen(false);
      // Hand off to the entry gate — it polls config during the in_progress
      // teardown and renders the setup wizard once status is "disabled".
      onConfigChange?.();
    } catch (e) {
      // Keep the dialog open so the backend's message (e.g. "delete all
      // StackSets before disabling") is read in context.
      setDisableErr(e.message);
    } finally {
      setDisabling(false);
    }
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{pageHeader}</motion.div>
      {config?.status === 'partial_failure' && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
            Some execution roles failed to provision
            {config.errorMessage ? `: ${config.errorMessage}` : ''}. Retry them under
            Execution roles below — affected accounts can&apos;t receive stacks until
            their role is provisioned.
          </Alert>
        </motion.div>
      )}
      <motion.div variants={sectionVariants}>
        <Box sx={{ ...actionRowSx, justifyContent: 'flex-end', mb: 2 }}>
          <FormControlLabel
            sx={controlLabelSx}
            control={<Switch checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />}
            label="Show deleted"
          />
        </Box>
      </motion.div>
      <motion.div variants={sectionVariants}>
        {/* listCardSx sets overflow:hidden to clip the table to the card's
            rounded corners; re-enable the x-axis so a wide row can scroll
            inside the card instead of being cut off. */}
        <TableContainer component={Paper} elevation={0} sx={{ ...listCardSx, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Name</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx}>Target OUs</TableCell>
                <TableCell sx={headCellSx}>Regions</TableCell>
                <TableCell sx={headCellSx}>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <Typography sx={emptyTextSx}>
                      No StackSets yet — create one to deploy a template across your OUs.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visible.map((r, index) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ ...rowSx(index), cursor: 'pointer', transition: 'background-color 0.2s ease, border-color 0.2s ease' }}
                  onClick={() => navigate(`/stacksets/${r.id}`)}
                >
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell sx={bodyCellSx}><Chip size="small" label={formatStatusLabel(r.status)} sx={statusChipSx(intentFromColor(statusColor(r.status)))} /></TableCell>
                  <TableCell sx={{ ...bodyCellSx, maxWidth: 320, wordBreak: 'break-word' }}>{parseOUs(r.targetOus).map((ou) => ou.name).join(', ')}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, maxWidth: 280, wordBreak: 'break-word' }}>{parseRegions(r.regions).join(', ')}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{new Date(r.updatedAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </motion.div>

      <motion.div variants={sectionVariants}>
        <Box sx={sectionGapSx}>
          <RoleStatusPanel
            adminAccountId={config?.adminAccountId}
            // Background config refresh (no full-page spinner) so a
            // partial_failure → enabled rollup updates the banner in place.
            onQuiesce={() => onConfigChange?.({ showSpinner: false })}
          />
        </Box>
      </motion.div>

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, ...sectionGapSx }}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Danger zone</Typography>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
            Disabling removes the execution role from every onboarded account and the
            administration role from the admin account. StackSet history in Prism is retained.
          </Alert>
          {liveCount > 0 && (
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              Delete all StackSets before disabling the service ({liveCount} remaining).
            </Typography>
          )}
          <Button color="error" variant="contained" disabled={liveCount > 0} onClick={openDisable}>
            Disable StackSets
          </Button>
        </Paper>
      </motion.div>

      <Dialog
        open={disableOpen}
        onClose={() => { if (!disabling) setDisableOpen(false); }}
        {...dialogProps}
      >
        <DialogTitle sx={dialogTitleSx}>Disable StackSets</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1, mb: 1 }}>
            Prism will remove CKPrism-StackSetExecutionRole from every onboarded account
            and CKPrism-StackSetAdministrationRole from the admin account
            {config?.adminAccountId ? ` (${config.adminAccountId})` : ''}.
          </Typography>
          <FormControlLabel
            sx={controlLabelSx}
            control={
              <Checkbox
                checked={deleteBucket}
                onChange={(e) => setDeleteBucket(e.target.checked)}
              />
            }
            label={`Also delete the template bucket${config?.templateBucket ? ` (${config.templateBucket})` : ''}`}
          />
          <Typography sx={{ ...emptyTextSx, fontSize: '0.75rem', display: 'block' }}>
            The bucket is only deleted if Prism created it during setup; pre-existing
            buckets are always left in place.
          </Typography>
          {disableErr && <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>{disableErr}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDisableOpen(false)} disabled={disabling}>Cancel</Button>
          <Button color="error" variant="contained" disabled={disabling} onClick={doDisable}>
            {disabling ? 'Disabling…' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
